import FormButtons from "components/FormButtons"
import { ChangeEvent, useEffect, useState, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { getTintVTOService } from "services/tintvto"
import { MerchantData } from "types"
import {
  Box,
  MultiSelect,
  MultiSelectOption,
  Option,
  Select,
  SingleSelect,
  SingleSelectOption,
  TextInput,
} from "@strapi/design-system"
import { useBrandsQuery } from "hooks/query/useBrandsQuery"

interface MerchantFormProps {
  merchant: MerchantData
  onChange: (data: MerchantData) => void
  loading: boolean
  handleSave: () => void
}

export const MerchantForm = (props: MerchantFormProps) => {
  const service = getTintVTOService()
  const merchant = props.merchant
  const [plans, setPlans] = useState([])
  const [users, setUsers] = useState([])
  const [isUsersLoading, setUsersLoading] = useState(true)
  const { t } = useTranslation()

  const validateForm = useCallback(() => {
    if (merchant.name?.trim().length === 0) return false

    if (merchant.email?.trim().length === 0) return false

    if (merchant.domain?.trim().length === 0) return false

    if (merchant.description?.trim().length === 0) return false

    if (merchant.planId?.trim().length === 0) return false

    if (!merchant.brandsIds || merchant.brandsIds.length === 0) return false

    if (merchant.cognitoSub.length === 0) return false

    return true
  }, [merchant])
  const [submitable, setSubmitable] = useState(validateForm())

  useEffect(() => {
    ;(async () => {
      setSubmitable(validateForm())
    })()
  }, [merchant, validateForm])

  const { data: brands, isLoading: isBrandsLoading } = useBrandsQuery()

  useEffect(() => {
    ;(async () => {
      const response = await service.plans()
      const plansJson = await response.json()

      setPlans(
        plansJson.map((pp: any) => {
          return {
            id: pp.id,
            name: pp.name,
          }
        })
      )

      const {
        data: { listUsers },
      } = await service.listUsers()
      setUsers(listUsers)
      setUsersLoading(false)
    })()
  }, [service])

  return (
    <>
      <Box style={{ paddingBottom: "18px" }}>
        <TextInput
          required
          label={t("name")}
          name="name"
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            props.onChange({
              ...merchant,
              name: e.target.value,
            })
            setSubmitable(validateForm())
          }}
          value={merchant.name}
        />
      </Box>
      <Box style={{ paddingBottom: "18px" }}>
        <TextInput
          required
          label={t("email")}
          name="email"
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            props.onChange({
              ...merchant,
              email: e.target.value,
            })
            setSubmitable(validateForm())
          }}
          value={merchant.email}
        />
      </Box>
      <Box style={{ paddingBottom: "18px" }}>
        <TextInput
          required
          label={t("domain")}
          name="domain"
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            props.onChange({
              ...merchant,
              domain: e.target.value,
            })
            setSubmitable(validateForm())
          }}
          value={merchant.domain}
        />
      </Box>
      <Box style={{ paddingBottom: "18px" }}>
        <TextInput
          required
          label={t("description")}
          name="description"
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            props.onChange({
              ...merchant,
              description: e.target.value,
            })
            setSubmitable(validateForm())
          }}
          value={merchant.description}
        />
      </Box>
      <Box style={{ paddingBottom: "18px" }}>
        <Select
          required
          disabled={!plans.length}
          label={t("plan")}
          placeholder={t("selectPlan")}
          name="plan"
          onChange={(value: string) => {
            props.onChange({
              ...merchant,
              planId: value,
            })
            setSubmitable(validateForm())
          }}
          value={merchant.planId}
        >
          {plans.map((plan: { id: string; name: string }) => (
            <Option key={plan.id} value={plan.id}>
              {plan.name}
            </Option>
          ))}
        </Select>
      </Box>
      <Box style={{ paddingBottom: "18px" }}>
        <MultiSelect
          label={t("brands")}
          required
          withTags
          value={merchant.brandsIds && merchant.brandsIds.map((item) => item.toString())}
          placeholder={t("selectBrands")}
          disabled={isBrandsLoading}
          onClear={() =>
            props.onChange({
              ...merchant,
              brandsIds: [],
            })
          }
          onChange={(brandsIds: string[]) =>
            props.onChange({
              ...merchant,
              brandsIds,
            })
          }
        >
          {brands?.map((brand: any) => (
            <MultiSelectOption key={brand.id} value={brand.id}>
              {brand.name}
            </MultiSelectOption>
          ))}
        </MultiSelect>
      </Box>
      <Box style={{ paddingBottom: "18px" }}>
        <SingleSelect
          label={t("user")}
          placeholder={t("selectUser")}
          required
          disabled={isUsersLoading}
          onChange={(value: string) =>
            props.onChange({
              ...merchant,
              cognitoSub: value,
            })
          }
          value={merchant.cognitoSub}
        >
          {users.map(({ username, sub }) => (
            <SingleSelectOption key={sub} value={sub}>
              {username}
            </SingleSelectOption>
          ))}
        </SingleSelect>
      </Box>

      <FormButtons
        saveDisabled={!submitable}
        loading={props.loading}
        onSave={props.handleSave}
        cancelLink={`/merchants`}
      />
    </>
  )
}
